import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';
import type { Bloque, Clase, EjercicioAsignado, Recurso } from '../../types';
import { Tarjeta } from '../ui/Tarjeta';
import { Boton } from '../ui/Boton';
import { Modal } from '../ui/Modal';
import { Aviso } from '../ui/Aviso';
import { CampoArea, CampoNumero, CampoTexto, Casilla } from '../ui/Campo';
import { PanelTiempo } from './PanelTiempo';
import { EditorEjercicio } from './EditorEjercicio';
import { ExploradorRecursos } from '../biblioteca/ExploradorRecursos';
import { FichaRecurso } from '../biblioteca/FichaRecurso';
import { NIVELES_INFO } from '../../data/niveles';
import { IDS_INVERSION_AEREA } from '../../data/constantes';
import { ejercicioDesdeRecurso, ejercicioPersonalizadoVacio, bloqueVacio } from '../../utils/clase';
import { nuevoId } from '../../utils/id';
import { MENSAJE_NIVEL_SUPERIOR, calcularAdvertencias, estadoTiempo, nivelSuperaClase } from '../../utils/reglas';
interface Props {
  clase: Clase;
  actualizar: (parcial: Partial<Clase>) => void;
  recursos: Recurso[];
  favoritos: string[];
  recientes: string[];
  alternarFavorito: (id: string) => void;
  registrarReciente: (id: string) => void;
}
interface Pendiente {
  recurso: Recurso;
  uidBloque: string;
  motivos: string[];
}
export function Paso3Bloques({
  clase,
  actualizar,
  recursos,
  favoritos,
  recientes,
  alternarFavorito,
  registrarReciente
}: Props) {
  const [bloqueDestino, setBloqueDestino] = useState<string | null>(null);
  const [ficha, setFicha] = useState<Recurso | null>(null);
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const [confirmacionPendiente, setConfirmacionPendiente] = useState(false);
  const [excesoConfirmado, setExcesoConfirmado] = useState(false);
  const advertencias = calcularAdvertencias(clase, recursos);
  const tiempo = estadoTiempo(clase);
  const coleccion = NIVELES_INFO[clase.nivel].coleccion;
  const porId = new Map(recursos.map(r => [r.id, r]));
  const setBloques = (bloques: Bloque[]) => actualizar({
    bloques
  });
  const cambiarBloque = (uid: string, parcial: Partial<Bloque>) => {
    setBloques(clase.bloques.map(b => b.uid === uid ? {
      ...b,
      ...parcial
    } : b));
  };
  const mover = (indice: number, delta: number) => {
    const destino = indice + delta;
    if (destino < 0 || destino >= clase.bloques.length) return;
    const copia = [...clase.bloques];
    const [item] = copia.splice(indice, 1);
    copia.splice(destino, 0, item);
    setBloques(copia);
  };
  const duplicarBloque = (uid: string) => {
    const indice = clase.bloques.findIndex(b => b.uid === uid);
    const original = clase.bloques[indice];
    const copia: Bloque = {
      ...original,
      uid: nuevoId('blq'),
      nombre: `${original.nombre} (copia)`,
      ejercicios: original.ejercicios.map(e => ({
        ...e,
        uid: nuevoId('ej')
      }))
    };
    const bloques = [...clase.bloques];
    bloques.splice(indice + 1, 0, copia);
    setBloques(bloques);
  };
  const eliminarBloque = (uid: string) => {
    setBloques(clase.bloques.filter(b => b.uid !== uid));
  };
  const agregarEjercicio = (uidBloque: string, ejercicio: EjercicioAsignado) => {
    setBloques(clase.bloques.map(b => b.uid === uidBloque ? {
      ...b,
      ejercicios: [...b.ejercicios, ejercicio]
    } : b));
  };
  const intentarAgregar = (recurso: Recurso, uidBloque: string) => {
    registrarReciente(recurso.id);
    const motivos: string[] = [];
    if (nivelSuperaClase(recurso, clase.nivel)) motivos.push(MENSAJE_NIVEL_SUPERIOR);
    if (IDS_INVERSION_AEREA.includes(recurso.id)) {
      motivos.push(`Inversión aérea. Prerrequisitos: ${recurso.prerrequisitos.join(' · ')}. ${recurso.seguridad}`);
    }
    if (recurso.dinamico) {
      motivos.push('Contenido dinámico o caída: confirmá que conocés y podés enseñar esta progresión, con salida, asistencia y condiciones de seguridad verificadas.');
    }
    if (motivos.length > 0) {
      setPendiente({
        recurso,
        uidBloque,
        motivos
      });
      setConfirmacionPendiente(!recurso.dinamico);
      return;
    }
    agregarEjercicio(uidBloque, ejercicioDesdeRecurso(recurso));
    setBloqueDestino(null);
  };
  const confirmarPendiente = () => {
    if (!pendiente) return;
    agregarEjercicio(pendiente.uidBloque, ejercicioDesdeRecurso(pendiente.recurso));
    setPendiente(null);
    setConfirmacionPendiente(false);
    setBloqueDestino(null);
  };
  const agregarDescenso = (id: string) => {
    const recurso = porId.get(id);
    if (!recurso) return;
    const bloqueTrepa = clase.bloques.find(b => b.ejercicios.some(e => e.recursoId && e.recursoId.startsWith('T')));
    const destino = bloqueTrepa?.uid || clase.bloques[0]?.uid;
    if (!destino) return;
    agregarEjercicio(destino, ejercicioDesdeRecurso(recurso));
  };
  return <div className="space-y-4" data-id="element-134" data-mp="31mhsckopaj22">
      <div className="sticky top-[68px] z-20 lg:top-[72px]" data-id="element-135" data-mp="2wm3jx1j6t73i">
        <div className="rounded-xl bg-lienzo pb-2" data-id="element-136" data-mp="2wksd0sug3j86">
          <PanelTiempo clase={clase} data-id="element-137" data-mp="2miop95gftsv5" />
        </div>
      </div>

      {tiempo.diferencia > 0 && !excesoConfirmado && <Aviso tipo="alerta" titulo="La clase excede la duración prevista" data-id="element-138" data-mp="2rib2hew9n93y">
          <p data-id="element-139" data-mp="2wft1odkhe9yr">
            Hay {tiempo.planificado} min planificados sobre {tiempo.objetivo} min. Podés dejarlo así
            confirmándolo.
          </p>
          <Boton className="mt-2" tamano="sm" onClick={() => setExcesoConfirmado(true)} data-id="element-140" data-mp="3195pwml90eqz">
            Confirmar el exceso
          </Boton>
        </Aviso>}

      {advertencias.some(a => a.id === 'trepa-descenso') && <Aviso tipo="alerta" titulo="Trepa sin descenso vinculado" data-id="element-141" data-mp="3f73qmp6ncco0">
          <p data-id="element-142" data-mp="3f4rc6m03kkfp">Toda trepa necesita un descenso planificado.</p>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-143" data-mp="3k4vt2d4x3h6g">
            <Boton tamano="sm" variante="contorno" onClick={() => agregarDescenso('T07')} data-id="element-144" data-mp="3k3ko49zqk3dr">
              Agregar T07 Descenso básico controlado
            </Boton>
            <Boton tamano="sm" variante="contorno" onClick={() => agregarDescenso('T08')} data-id="element-145" data-mp="1psfpxl7f4j5t">
              Agregar T08 Descenso segmentado
            </Boton>
            <Boton tamano="sm" variante="texto" onClick={() => {
          const destino = clase.bloques[0]?.uid;
          if (!destino) return;
          agregarEjercicio(destino, {
            ...ejercicioPersonalizadoVacio(),
            nombre: 'Descenso personalizado'
          });
        }} data-id="element-146" data-mp="1csn1htey1p30">
              Descenso personalizado
            </Boton>
          </div>
        </Aviso>}

      {advertencias.filter(a => a.id !== 'trepa-descenso' && a.id !== 'tiempo' && a.id !== 'logistica').map(a => <Aviso key={a.id} tipo={a.tipo} titulo={a.titulo} data-id="element-147" data-mp="25i3pmevj6jtf">
            {a.detalle}
          </Aviso>)}

      <ul className="space-y-4" data-id="element-148" data-mp="2jh3d3j7b4so0">
        {clase.bloques.map((bloque, indice) => <li key={bloque.uid} data-id="element-149" data-mp="2fg9qqu9ijlx8" data-mpi={indice}>
            <Tarjeta data-id="element-150" data-mp="2fcvr3ts8hqth">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr]" data-id="element-151" data-mp="2y3ilfu4s2sn4">
                <CampoTexto etiqueta="Nombre del bloque" value={bloque.nombre} onChange={e => cambiarBloque(bloque.uid, {
              nombre: e.target.value
            })} data-id="element-152" data-mp="2y1fxrgr3lblk" />
                <CampoNumero etiqueta="Minutos" min={0} max={180} value={bloque.minutos} onChange={e => cambiarBloque(bloque.uid, {
              minutos: Number(e.target.value)
            })} data-id="element-153" data-mp="2u1fad6m0tq2d" />
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-154" data-mp="26ccsbxnh5v7c">
                <CampoTexto etiqueta="Objetivo del bloque" value={bloque.objetivo} onChange={e => cambiarBloque(bloque.uid, {
              objetivo: e.target.value
            })} data-id="element-155" data-mp="26a9tco4cgrqh" />
                <CampoArea etiqueta="Notas" rows={2} value={bloque.notas} onChange={e => cambiarBloque(bloque.uid, {
              notas: e.target.value
            })} data-id="element-156" data-mp="21djeg44a026n" />
              </div>

              <div className="mt-3 flex flex-wrap gap-2" data-id="element-157" data-mp="2bwpvc16xap3n">
                <Boton tamano="sm" onClick={() => setBloqueDestino(bloque.uid)} data-id="element-158" data-mp="27tjtr6ur9m3i">
                  <Plus className="h-4 w-4" aria-hidden="true" data-id="element-159" data-mp="22vi8qgdrrcpe" />
                  Agregar desde biblioteca
                </Boton>
                <Boton tamano="sm" variante="contorno" onClick={() => agregarEjercicio(bloque.uid, ejercicioPersonalizadoVacio())} data-id="element-160" data-mp="1xwxb8opwj74n">
                  Agregar personalizado
                </Boton>
                <Boton tamano="sm" variante="texto" onClick={() => duplicarBloque(bloque.uid)} data-id="element-161" data-mp="1k50iqjkbmv7q">
                  <Copy className="h-4 w-4" aria-hidden="true" data-id="element-162" data-mp="1f5e8mizshv35" />
                  Duplicar
                </Boton>
                <Boton tamano="sm" variante="texto" disabled={indice === 0} onClick={() => mover(indice, -1)} data-id="element-163" data-mp="1a6b18rwc7cs0">
                  <ArrowUp className="h-4 w-4" aria-hidden="true" data-id="element-164" data-mp="3qs7yy0jqi6m3" />
                  Mover arriba
                </Boton>
                <Boton tamano="sm" variante="texto" disabled={indice === clase.bloques.length - 1} onClick={() => mover(indice, 1)} data-id="element-165" data-mp="3vtf55v68axqn">
                  <ArrowDown className="h-4 w-4" aria-hidden="true" data-id="element-166" data-mp="331c0wlooqf6e" />
                  Mover abajo
                </Boton>
                <Boton tamano="sm" variante="texto" onClick={() => eliminarBloque(bloque.uid)} data-id="element-167" data-mp="3c44tqtdimyyc">
                  <Trash2 className="h-4 w-4 text-alerta" aria-hidden="true" data-id="element-168" data-mp="3gutl88p73vxh" />
                  Eliminar
                </Boton>
              </div>

              {bloque.ejercicios.length === 0 ? <p className="mt-3 rounded-xl border border-dashed border-linea p-3 text-sm text-tinta-tenue" data-id="element-169" data-mp="2oeiu64t8ehpt">
                  Todavía no hay ejercicios en este bloque.
                </p> : <ul className="mt-3 space-y-2" data-id="element-170" data-mp="2nrhcbyzdrosu">
                  {bloque.ejercicios.map(ej => {
              const recurso = ej.recursoId ? porId.get(ej.recursoId) || null : null;
              return <EditorEjercicio key={ej.uid} ejercicio={ej} recurso={recurso} pedirLado={Boolean(recurso?.bilateral) && ej.lado === 'No aplica'} onCambiar={parcial => cambiarBloque(bloque.uid, {
                ejercicios: bloque.ejercicios.map(e => e.uid === ej.uid ? {
                  ...e,
                  ...parcial
                } : e)
              })} onDuplicar={() => cambiarBloque(bloque.uid, {
                ejercicios: [...bloque.ejercicios, {
                  ...ej,
                  uid: nuevoId('ej')
                }]
              })} onQuitar={() => cambiarBloque(bloque.uid, {
                ejercicios: bloque.ejercicios.filter(e => e.uid !== ej.uid)
              })} data-id="element-171" data-mp="2wrcp288l3ruz" />;
            })}
                </ul>}
            </Tarjeta>
          </li>)}
      </ul>

      <Boton variante="contorno" onClick={() => setBloques([...clase.bloques, bloqueVacio('Bloque nuevo')])} data-id="element-172" data-mp="l5u24pe6am2m">
        <Plus className="h-4 w-4" aria-hidden="true" data-id="element-173" data-mp="g3wd1ej3cx1r" />
        Agregar bloque
      </Boton>

      <Modal abierto={Boolean(bloqueDestino)} titulo="Agregar desde biblioteca" descripcion={`Bloque: ${clase.bloques.find(b => b.uid === bloqueDestino)?.nombre || ''}`} onCerrar={() => setBloqueDestino(null)} ancho="lg" data-id="element-174" data-mp="c3kviam0zcui">
        <ExploradorRecursos recursos={recursos} favoritos={favoritos} recientes={recientes} alternarFavorito={alternarFavorito} onAbrirFicha={r => {
        registrarReciente(r.id);
        setFicha(r);
      }} nivelClase={clase.nivel} coleccionNivel={coleccion} accionSecundaria={r => bloqueDestino && intentarAgregar(r, bloqueDestino)} etiquetaAccion="Agregar al bloque" data-id="element-175" data-mp="1k1yd5brm6pb" />
      </Modal>

      <FichaRecurso recurso={ficha} abierto={Boolean(ficha)} onCerrar={() => setFicha(null)} favorito={ficha ? favoritos.includes(ficha.id) : false} onFavorito={() => ficha && alternarFavorito(ficha.id)} onAgregarAClase={ficha && bloqueDestino ? () => {
      intentarAgregar(ficha, bloqueDestino);
      setFicha(null);
    } : undefined} recursos={recursos} onAbrirRelacionado={r => setFicha(r)} data-id="element-176" data-mp="6hkb76swl62l" />

      <Modal abierto={Boolean(pendiente)} titulo="Revisá antes de agregar" descripcion={pendiente ? `${pendiente.recurso.id} · ${pendiente.recurso.nombre}` : undefined} onCerrar={() => {
      setPendiente(null);
      setConfirmacionPendiente(false);
    }} ancho="sm" acciones={<>
            <Boton variante="contorno" onClick={() => {
        setPendiente(null);
        setConfirmacionPendiente(false);
      }} data-id="element-178" data-mp="20riml2is4h6t">
              Cancelar
            </Boton>
            <Boton disabled={!confirmacionPendiente} onClick={confirmarPendiente} data-id="element-179" data-mp="3q55de5cq9u8v">
              Agregar bajo mi criterio
            </Boton>
          </>} data-id="element-177" data-mp="2xggl8vtngz99">
        <div className="space-y-3" data-id="element-180" data-mp="3l5srbmtokr7c">
          {pendiente?.motivos.map(m => <Aviso key={m} tipo="aviso" data-id="element-181" data-mp="kc4r0myeatm9">
              {m}
            </Aviso>)}
          {pendiente?.recurso.dinamico ? <Casilla etiqueta="Conozco y puedo enseñar esta progresión; verifiqué la preparación, la salida, la asistencia y las condiciones de seguridad" checked={confirmacionPendiente} onChange={setConfirmacionPendiente} data-id="element-182" data-mp="pbr671xsajxp" /> : <Casilla etiqueta="Revisé los prerrequisitos y decido incorporarlo bajo mi criterio profesional" checked={confirmacionPendiente} onChange={setConfirmacionPendiente} data-id="element-183" data-mp="137mi2ntrmurl" />}
        </div>
      </Modal>
    </div>;
}